import axios from "axios";
import { getToken } from "Shared/baseURL";
import { attachApiInterceptors } from "Shared/apiInterceptors";

const resolveNotificationServiceBase = () => {
  const explicitBase =
    import.meta.env.VITE_NOTIFICATION_SERVICE_BASE ||
    import.meta.env.VITE_NMS_BASE_URL;

  if (explicitBase) {
    return String(explicitBase).replace(/\/$/, "");
  }

  const base = import.meta.env.VITE_BASEURL || "";
  const normalizedBase = String(base).replace(/\/$/, "");
  return normalizedBase ? `${normalizedBase}/nms` : "/api/nms";
};

const NOTIFICATION_SERVICE_BASE = resolveNotificationServiceBase().replace(
  /\/$/,
  "",
);

const notificationServiceClient = axios.create({
  baseURL: NOTIFICATION_SERVICE_BASE,
});
attachApiInterceptors(notificationServiceClient, "notificationServiceClient");

const buildNotificationServicePath = (path = "") => {
  if (typeof path !== "string") return "";
  return path.replace(/^\/+/, "");
};

const toInt64QueryValue = (value) => {
  if (value === undefined || value === null || value === "") return undefined;

  const normalized = String(value).trim();
  return /^\d+$/.test(normalized) ? normalized : undefined;
};

const normalizePageableParam = ({ page = 0, size = 20, sort = [] } = {}) => ({
  page: Math.max(0, Number(page) || 0),
  size: Math.max(1, Number(size) || 20),
  sort: Array.isArray(sort) ? sort : sort ? [sort] : [],
});

const getTemplatePageContent = (payload) =>
  Array.isArray(payload?.content) ? payload.content : [];

const getTemplateVersion = (template) => Number(template?.version) || 0;

const buildUpdateTemplatePayload = (template = {}) => {
  const updatePayload = { ...template };

  [
    "id",
    "type",
    "createdAt",
    "updatedAt",
    "createdBy",
    "updatedBy",
    "published",
  ].forEach((key) => {
    delete updatePayload[key];
  });

  return updatePayload;
};

const withAuthHeaders = async (config = {}) => {
  const token = await getToken();
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(token ? { Authorization: token } : {}),
    },
  };
};

// --- Normalizers ---
const normalizeNotification = (payload) => {
  if (!payload || typeof payload !== "object") return {};
  const data = payload.data || {};
  return {
    ...payload,
    id: payload.id || payload.messageId || null,
    title: data.title || payload.title || payload.subject || "",
    body: data.body || payload.body || payload.content || "",
    isRead: payload.readAt != null || !!payload.isRead,
    createdAt: payload.createdAt || payload.timestamp || null,
  };
};

const normalizeNotificationPage = (payload) => {
  const content = Array.isArray(payload?.content) ? payload.content : [];
  const pageNumber = payload?.number ?? payload?.pageable?.pageNumber ?? 0;
  const pageSize = payload?.size ?? payload?.pageable?.pageSize ?? content.length;
  const numberOfElements = payload?.numberOfElements ?? content.length;
  const totalElements = payload?.totalElements ?? numberOfElements;
  const totalPages = payload?.totalPages ?? (payload?.hasNext ? pageNumber + 2 : pageNumber + 1);
  return {
    notifications: content.map(normalizeNotification),
    pagination: {
      page: pageNumber + 1,
      pageIndex: pageNumber,
      size: pageSize,
      numberOfElements,
      total: totalElements,
      totalPages,
      hasNext: Boolean(payload?.hasNext),
      hasPrevious: Boolean(payload?.hasPrevious),
      isFirst: Boolean(payload?.first),
      isLast: Boolean(payload?.last),
      isEmpty: Boolean(payload?.empty),
    },
  };
};

const defaultNotificationPreferences = {
  pushNotifications: true,
  inAppNotifications: true,
  emailNotifications: true,
  weeklyDigest: true,
  studyReminders: true,
};

export const normalizeNotificationPreferences = (
  payload = {},
  fallbackPreferences = defaultNotificationPreferences,
) => {
  const source = payload?.preferences || payload || {};

  return Object.entries(defaultNotificationPreferences).reduce(
    (preferences, [key, defaultValue]) => {
      const legacyValue =
        key === "emailNotifications"
          ? source.newsletterSubscribed
          : key === "inAppNotifications"
            ? source.connectionRequests
            : undefined;
      const nextValue =
        typeof source[key] === "boolean"
          ? source[key]
          : typeof legacyValue === "boolean"
            ? legacyValue
            : typeof fallbackPreferences[key] === "boolean"
              ? fallbackPreferences[key]
              : defaultValue;

      return {
        ...preferences,
        [key]: nextValue,
      };
    },
    {},
  );
};

// --- API Methods ---
export const listNotifications = async ({
  page = 0,
  size = 20,
  isRead,
  recipient,
  producer,
} = {}) => {
  const recipientQueryValue = toInt64QueryValue(recipient);
  const params = {
    page,
    size,
    ...(isRead !== undefined ? { isRead } : {}),
    ...(recipientQueryValue !== undefined
      ? { recipient: recipientQueryValue }
      : {}),
    ...(producer !== undefined ? { producer } : {}),
  };

  const config = await withAuthHeaders({ params });
  const response = await notificationServiceClient.get(
    buildNotificationServicePath("v1/notifications"),
    config,
  );
  return normalizeNotificationPage(response.data);
};

export const getNotificationPreferences = async (userId) => {
  const normalizedUserId = toInt64QueryValue(userId);
  if (!normalizedUserId) throw new Error("Valid userId is required");

  const config = await withAuthHeaders();
  const response = await notificationServiceClient.get(
    buildNotificationServicePath(
      `v1/users/${normalizedUserId}/notification-preferences`,
    ),
    config,
  );
  return normalizeNotificationPreferences(response.data);
};

export const updateNotificationPreferences = async (userId, preferences) => {
  const normalizedUserId = toInt64QueryValue(userId);
  if (!normalizedUserId) throw new Error("Valid userId is required");

  const config = await withAuthHeaders({
    headers: {
      "Content-Type": "application/json",
    },
  });
  const response = await notificationServiceClient.patch(
    buildNotificationServicePath(
      `v1/users/${normalizedUserId}/notification-preferences`,
    ),
    normalizeNotificationPreferences(preferences),
    config,
  );
  return response.data && typeof response.data === "object"
    ? normalizeNotificationPreferences(response.data, preferences)
    : normalizeNotificationPreferences(preferences);
};

export const markNotificationAsRead = async (messageId) => {
  if (!messageId) throw new Error("messageId is required");
  const config = await withAuthHeaders();
  const response = await notificationServiceClient.put(
    buildNotificationServicePath(`v1/notifications/${messageId}/read`),
    null,
    config,
  );
  return response.data;
};

export const getNotificationById = async (messageId) => {
  if (!messageId) throw new Error("messageId is required");
  const config = await withAuthHeaders();
  const response = await notificationServiceClient.get(
    buildNotificationServicePath(`v1/notifications/${messageId}`),
    config,
  );
  return normalizeNotification(response.data);
};

export const markAllNotificationsAsRead = async () => {
  const config = await withAuthHeaders();
  const response = await notificationServiceClient.put(
    buildNotificationServicePath("v1/notifications/read"),
    null,
    config,
  );
  return response.data;
};

export const getNotificationTemplateMetadataSchemas = async () => {
  const config = await withAuthHeaders();
  const response = await notificationServiceClient.get(
    buildNotificationServicePath("v1/templates/metadata/schemas"),
    config,
  );
  return response.data;
};

export const searchNotificationTemplates = async ({
  type,
  locale,
  published,
  pageable,
} = {}) => {
  const normalizedPageable = normalizePageableParam(pageable);
  const params = {
    ...(type ? { type } : {}),
    ...(locale ? { locale } : {}),
    ...(published !== undefined ? { published } : {}),
    ...normalizedPageable,
  };
  const config = await withAuthHeaders({ params });
  const response = await notificationServiceClient.get(
    buildNotificationServicePath("v1/templates/search"),
    config,
  );
  return response.data;
};

export const createNotificationTemplate = async (payload) => {
  const config = await withAuthHeaders();
  const response = await notificationServiceClient.post(
    buildNotificationServicePath("v1/templates"),
    payload,
    config,
  );
  return response.data;
};

export const updateNotificationTemplate = async (id, payload) => {
  if (!id) throw new Error("Template id is required");
  const config = await withAuthHeaders();
  const response = await notificationServiceClient.put(
    buildNotificationServicePath(`v1/templates/${id}`),
    payload,
    config,
  );
  return response.data;
};

export const publishNotificationTemplate = async (id) => {
  if (!id) throw new Error("Template id is required");
  const config = await withAuthHeaders();
  const response = await notificationServiceClient.post(
    buildNotificationServicePath(`v1/templates/${id}/publish`),
    null,
    config,
  );
  return response.data;
};

export const findNotificationTemplate = async ({ type, locale = "en" } = {}) => {
  if (!type) throw new Error("Template type is required");

  const response = await searchNotificationTemplates({
    type,
    locale,
    pageable: {
      page: 0,
      size: 50,
      sort: ["version,desc"],
    },
  });

  return (
    getTemplatePageContent(response)
      .filter(
        (template) =>
          template?.type === type &&
          String(template?.locale || "en") === String(locale || "en"),
      )
      .sort((a, b) => getTemplateVersion(b) - getTemplateVersion(a))[0] ||
    null
  );
};

export const upsertNotificationTemplate = async (
  template,
  { publish = true } = {},
) => {
  if (!template?.type) throw new Error("Template type is required");

  const locale = template.locale || "en";
  const existingTemplate = await findNotificationTemplate({
    type: template.type,
    locale,
  });

  const savedTemplate = existingTemplate?.id
    ? await updateNotificationTemplate(
        existingTemplate.id,
        buildUpdateTemplatePayload(template),
      )
    : await createNotificationTemplate(template);

  const templateId = savedTemplate?.id || existingTemplate?.id;
  const publishStatus = publish
    ? await publishNotificationTemplate(templateId)
    : null;

  return {
    template: savedTemplate,
    created: !existingTemplate?.id,
    published: Boolean(publish),
    publishStatus,
  };
};
